import type { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualUnavailabilityByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import type { NameState } from "permissions/_views/targets/permission-target-state";
import type Permission from "permissions/permission";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Whether the directory named each person a page shows — a grantee, a grantor, the person a page is
 * about — and if not, why (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Target and person
 * states"). The individuals lookup answers each user id it was asked about: found, or recorded in the
 * store as `missing` or `unauthorized`. An id it has not answered is loading, or failed when the lookup
 * failed.
 */

export type PersonNames = Readonly<{
  /** Each asked-about user id's state, keyed by the id in lower case. */
  states: ReadonlyMap<string, NameState>;
  /** The individuals found, keyed the same way, for an avatar or an email. */
  individuals: ReadonlyMap<string, Individual>;
  /** Asks the directory again, for the Retry a failed lookup offers. */
  retry: () => void;
}>;

const LOADING: NameState = { state: "loading" };

/** The state of one person, under the key `personNameStates` used. */
export function personNameState(names: Pick<PersonNames, "states">, userId: string): NameState {
  return names.states.get(userId.toLowerCase()) ?? LOADING;
}

/** The state of the person who removed a grant, or nothing when no person did (#6825). */
export function removerNameState(names: Pick<PersonNames, "states">, grant: Permission): NameState | undefined {
  return grant.disabledByUserId ? personNameState(names, grant.disabledByUserId.value) : undefined;
}

/**
 * Each id's state from what the lookup returned and what the store recorded as unavailable. A person
 * the directory returned with neither a name nor an email cannot be named by this viewer, and is drawn
 * as one they can't see rather than as a blank.
 */
export function personNameStates(
  userIds: Iterable<string>,
  found: readonly Individual[],
  unavailable: Readonly<Record<string, UnavailableReason>>,
  failed: boolean,
): ReadonlyMap<string, NameState> {
  const byUserId = new Map(found.map((individual) => [individual.userId.value.toLowerCase(), individual]));
  const reasons = new Map(Object.entries(unavailable).map(([id, reason]) => [id.toLowerCase(), reason]));
  const states = new Map<string, NameState>();
  for (const userId of userIds) {
    const key = userId.toLowerCase();
    const individual = byUserId.get(key);
    const name = individual ? individual.fullName || individual.email?.value : undefined;
    const reason = reasons.get(key);
    if (name) states.set(key, { state: "found", name });
    else if (individual) states.set(key, { state: "unauthorized" });
    else if (reason) states.set(key, { state: reason });
    else if (failed) states.set(key, { state: "failed" });
    else states.set(key, LOADING);
  }
  return states;
}

/**
 * Looks up `userIds` in the directory and answers each with its state. Pass a stable collection: a
 * fresh one each render is a fresh lookup argument.
 */
export function usePersonNames(userIds: ReadonlySet<string>): PersonNames {
  const query = useGetIndividualsByUserIdsQuery(userIds.size > 0 ? { userIds: [...userIds] } : skipToken);
  const unavailable = useSelector(getIndividualUnavailabilityByUserIds);
  const individuals = useMemo(() => {
    const map = new Map<string, Individual>();
    for (const individual of Individual.fromJSONArray(query.data, { allowEmpty: true }) ?? []) {
      if (individual.userId) map.set(individual.userId.value.toLowerCase(), individual);
    }
    return map;
  }, [query.data]);
  const states = useMemo(
    () => personNameStates(userIds, [...individuals.values()], unavailable, query.isError),
    [userIds, individuals, unavailable, query.isError],
  );
  // A skipped query has nothing to refetch, and RTK throws when asked to.
  const { refetch } = query;
  const asked = userIds.size > 0;
  const retry = useCallback(() => {
    if (asked) void refetch();
  }, [asked, refetch]);
  return { states, individuals, retry };
}
